import { cloneDeep, groupBy, orderBy } from 'lodash';
import { CompetencyScore, InterviewScore, TechnicalValidationCompetency } from '@/types/api';
import { GradingRubric } from '@/types/api/gradingRubric';
import {
  CompetencyScoreInputTypeEnum,
  TechnicalChallengeCompetencyScore,
} from '@/types/api/technicalChallenge';
import { yesOrNoOptions } from './dropdownOptions';
import {
  getRangeOptions,
  TECHNICAL_VALIDATION_COMPETENCIES,
  TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
} from './interviews';

const getStringfiedValue = (score: number, type: CompetencyScoreInputTypeEnum): string => {
  if (type === CompetencyScoreInputTypeEnum.NUMERIC_RANGE) {
    return String(score);
  }

  return score === 0 ? 'No' : 'Yes';
};

export const getScoreValueBasedOnMaxScoreValue = ({
  scoreValue,
  maxScoreValue,
  dropdownOptions,
}: {
  scoreValue: number;
  maxScoreValue?: number;
  dropdownOptions: string[];
}): number => {
  if (!maxScoreValue) {
    return scoreValue;
  }

  const isBooleanInput = dropdownOptions.includes('Yes');

  if (isBooleanInput) {
    return scoreValue ? maxScoreValue : scoreValue;
  }

  return Math.min(scoreValue, maxScoreValue);
};

const getOptionsFromCompetencyScore = ({
  inputType,
  maxScoreValue,
}: {
  inputType: CompetencyScoreInputTypeEnum;
  maxScoreValue?: number;
}): string[] => {
  if (inputType === CompetencyScoreInputTypeEnum.BOOLEAN) {
    return yesOrNoOptions;
  }
  return getRangeOptions(maxScoreValue);
};

const getGradingRubricTechnicalValidationScore = (
  competencyScore: TechnicalChallengeCompetencyScore
): CompetencyScore => {
  return {
    name: competencyScore.scoreEnum || '',
    label: competencyScore.question,
    value: TECHNICAL_VALIDATION_DROPDOWN_PLACEHOLDER,
    options: getOptionsFromCompetencyScore(competencyScore),
    technicalChallengeCompetencyScoreId: competencyScore.id,
    maxScoreValue: competencyScore.maxScoreValue,
  };
};

const getReviewTechnicalValidationScore = (
  competencyScore: InterviewScore,
  weight?: number,
  inputType?: CompetencyScoreInputTypeEnum
): CompetencyScore => {
  const { technicalChallengeCompetencyScore } = competencyScore;

  const options = technicalChallengeCompetencyScore
    ? getOptionsFromCompetencyScore(technicalChallengeCompetencyScore)
    : [];

  // Convert weighted score from database to raw score for display
  const rawScore = competencyScore.score / (weight || 1);

  return {
    id: competencyScore.id,
    name: competencyScore.scoreType || '',
    label: technicalChallengeCompetencyScore?.question || '',
    value: getStringfiedValue(rawScore, inputType || CompetencyScoreInputTypeEnum.BOOLEAN),
    scoreValue: rawScore, // Store raw score
    maxScoreValue: technicalChallengeCompetencyScore?.maxScoreValue,
    options,
    technicalChallengeCompetencyScoreId: competencyScore.id,
  };
};

export const convertGradingRubricIntoTechnicalValidationCompetencies = (
  gradingRubric?: GradingRubric
): any => {
  if (!gradingRubric) {
    return cloneDeep(TECHNICAL_VALIDATION_COMPETENCIES);
  }

  const competencyScoresGroupedByCompetencyName = groupBy(
    gradingRubric.competencyScores,
    'competency.name'
  );

  const gradingRubricCompetencyNames = Object.keys(competencyScoresGroupedByCompetencyName);

  const competencies = gradingRubricCompetencyNames.map((competencyName) => {
    const competencyScores = competencyScoresGroupedByCompetencyName[competencyName];
    const sortedCompetencyScores = orderBy(
      competencyScores,
      ['technicalChallengeCompetencyScore.question'],
      ['asc']
    );

    const [firstScore] = competencyScores;

    return {
      name: firstScore.competency?.name || '',
      additionalInfo: '',
      weight: firstScore.competency?.weight || 1,
      scores: sortedCompetencyScores.map(getGradingRubricTechnicalValidationScore),
    };
  });

  return orderBy(competencies, ['name'], ['asc']);
};

export const convertReviewIntoTechnicalValidationCompetencies = ({
  competencies,
}: {
  competencies: TechnicalValidationCompetency[];
}): any => {
  const sortedCompetencies = orderBy(competencies, ['competency.name'], ['asc']);
  const formattedCompetencies = sortedCompetencies.map((reviewCompetency) => {
    const { additionalInfo, scores } = reviewCompetency;
    const competency = reviewCompetency?.competency;

    const interviewScores = scores as unknown as InterviewScore[];
    const sortedScores = orderBy(
      interviewScores,
      ['technicalChallengeCompetencyScore.question'],
      ['asc']
    );

    return {
      name: competency?.name || '',
      additionalInfo,
      weight: competency?.weight || 1,
      scores: sortedScores.map((score) =>
        getReviewTechnicalValidationScore(
          score,
          competency?.weight || 1,
          score?.technicalChallengeCompetencyScore?.inputType
        )
      ),
    };
  });

  return formattedCompetencies;
};
